import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Shield, X, Search, Lock, Globe, AlertTriangle, AlertCircle, Info, Check, Plus,
  LucideIcon,
} from 'lucide-react';

/**
 * Shared, reusable component kit for the Admin Management page (Approval Access WP9).
 * WP9a builds these against Tab A's needs; WP9b (Roles) and WP9c (Delegations) consume
 * them without restyling. Every element renders generic design class names that are
 * scoped under `.admin-mgmt` in AdminManagement.css, so nothing leaks to other pages.
 *
 * Consumer map (name → who uses it):
 *   Avatar            — roster + pickers (9a); role cards, delegation groups (9b/9c)
 *   StatusPill        — roster Status (9a: active/inactive); delegations (9c: scheduled/expired/revoked)
 *   SuperAdminMarker  — roster Role cell (9a); role cards + editor header (9b)
 *   ScopeBadge        — role editor toggle rows (9b)
 *   CountChip         — roster Delegations cell (9a); deep-links to Tab C (9c)
 *   ToggleSwitch      — role editor permission rows incl. disabled + violet locked-on (9b)
 *   SegmentedControl  — Add Delegation grant-type switch (9c)
 *   TabBar            — page frame tabs (9a); reusable on future v2 pages
 *   SearchablePicker  — Add/Edit admin linked-employee picker w/ None (9a);
 *                       supervisor + active-admin variants (9c) via the `options` prop
 *   MultiSelectPicker — individual-employees delegation picker (9c)
 *   WarnBanner        — supervisor-scope-without-employee warning (9a); reminders (9c)
 *   ErrorBanner       — last-super-admin / duplicate blocking errors (9a/9c)
 *   InlineError       — field-level blocking errors, e.g. duplicate email (9a)
 *   ModalShell        — Add/Edit + confirm dialogs (9a); role + delegation modals (9b/9c)
 */

// ── Avatar ───────────────────────────────────────────────────────────────────
export function initialsOf(first?: string, last?: string): string {
  return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase();
}

export const Avatar: React.FC<{ first?: string; last?: string; sm?: boolean }> = ({ first, last, sm }) => (
  <span className={`av${sm ? ' sm' : ''}`}>{initialsOf(first, last)}</span>
);

// ── Status pill ───────────────────────────────────────────────────────────────
export type StatusVariant = 'active' | 'inactive' | 'scheduled' | 'expired' | 'revoked';
const STATUS_LABEL: Record<StatusVariant, string> = {
  active: 'Active', inactive: 'Inactive', scheduled: 'Scheduled', expired: 'Expired', revoked: 'Revoked',
};
export const StatusPill: React.FC<{ variant: StatusVariant; label?: string }> = ({ variant, label }) => (
  <span className={`pill ${variant}`}><span className="dot" />{label ?? STATUS_LABEL[variant]}</span>
);

// ── Super-admin marker ────────────────────────────────────────────────────────
export const SuperAdminMarker: React.FC<{ short?: boolean }> = ({ short }) => (
  <span className="super-mark"><Shield />{short ? 'Super' : 'Super Admin'}</span>
);

// ── Scope badge (WP9b) ────────────────────────────────────────────────────────
export const ScopeBadge: React.FC<{ scope: 'scoped' | 'global' }> = ({ scope }) => (
  scope === 'scoped'
    ? <span className="scope-badge scoped"><Lock />In scope</span>
    : <span className="scope-badge global"><Globe />Org-wide</span>
);

// ── Count chip ────────────────────────────────────────────────────────────────
export const CountChip: React.FC<{
  count: number; unit?: string; onClick?: () => void; title?: string;
}> = ({ count, unit = 'grant', onClick, title }) => {
  if (!count) return <span className="count-chip zero">None</span>;
  const label = `${count} ${unit}${count === 1 ? '' : 's'}`;
  return (
    <button type="button" className="count-chip" onClick={onClick} title={title} disabled={!onClick}>
      {label}
    </button>
  );
};

// ── Toggle / switch (disabled + violet locked-on variants for WP9b) ───────────
export const ToggleSwitch: React.FC<{
  checked: boolean; onChange?: (checked: boolean) => void; disabled?: boolean; locked?: boolean;
  ariaLabel?: string;
}> = ({ checked, onChange, disabled, locked, ariaLabel }) => (
  <label className={`switch${disabled ? ' disabled' : ''}${locked ? ' locked' : ''}`}>
    <input
      type="checkbox"
      checked={locked ? true : checked}
      disabled={disabled || locked}
      aria-label={ariaLabel}
      onChange={(e) => onChange?.(e.target.checked)}
    />
    <span className="track" />
  </label>
);

// ── Segmented control (WP9c) ──────────────────────────────────────────────────
export interface SegOption<T extends string> { value: T; label: string; icon?: LucideIcon; }
export function SegmentedControl<T extends string>({ options, value, onChange }: {
  options: SegOption<T>[]; value: T; onChange: (v: T) => void;
}): React.ReactElement {
  return (
    <div className="segmented">
      {options.map((o) => {
        const Icon = o.icon;
        return (
          <button key={o.value} type="button" className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)}>
            {Icon && <Icon />}{o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Tab bar ───────────────────────────────────────────────────────────────────
export interface TabDef<K extends string> { key: K; label: string; icon: LucideIcon; count?: number; }
export function TabBar<K extends string>({ tabs, active, onChange }: {
  tabs: TabDef<K>[]; active: K; onChange: (k: K) => void;
}): React.ReactElement {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => {
        const Icon = t.icon;
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={t.key === active}
            className={`tab${t.key === active ? ' on' : ''}`}
            onClick={() => onChange(t.key)}
            type="button"
          >
            <Icon />{t.label}
            {t.count !== undefined && <span className="tcount">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Banners / inline messages ─────────────────────────────────────────────────
export const WarnBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="warn-banner"><AlertTriangle />{<div>{children}</div>}</div>
);
export const ErrorBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="err-banner"><AlertCircle />{<div>{children}</div>}</div>
);
export const InlineError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="inline-err"><AlertCircle />{<span>{children}</span>}</div>
);
export const PreviewLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="preview-line"><Info />{<div>{children}</div>}</div>
);

// ── Searchable single-select picker ───────────────────────────────────────────
export interface PickerOption {
  id: number | string;
  name: string;
  sub?: string;
  initials?: string;
}

/**
 * Positions a picker menu with position:fixed from the anchor's rect so it
 * floats OVER any overflow context. Inside the Add/Edit modal, an absolute
 * menu expanded the scrollable modal body (scrollbars mid-modal — bad UX);
 * fixed positioning lets it overhang the modal instead. Flips above the
 * anchor when there isn't room below, and repositions on scroll (capture
 * phase, so the modal body's own scroll counts) and resize.
 * NOTE: .modal-bg's backdrop-filter makes it the containing block for fixed
 * descendants, but it is inset:0 full-viewport, so coordinates map 1:1.
 */
const MENU_MAX_H = 230; // keep in sync with .picker-menu max-height
const useFloatingMenu = (open: boolean, anchorRef: React.RefObject<HTMLDivElement>): React.CSSProperties | undefined => {
  const [style, setStyle] = useState<React.CSSProperties | undefined>(undefined);
  useLayoutEffect(() => {
    if (!open) { setStyle(undefined); return; }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const roomBelow = window.innerHeight - r.bottom;
      const flip = roomBelow < MENU_MAX_H + 12 && r.top > MENU_MAX_H + 12;
      setStyle({
        position: 'fixed',
        left: r.left,
        width: r.width,
        right: 'auto',
        zIndex: 300, // above .modal-bg (200)
        ...(flip
          ? { top: 'auto', bottom: window.innerHeight - r.top + 5 }
          : { top: r.bottom + 5, bottom: 'auto' }),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef]);
  return style;
};
export const SearchablePicker: React.FC<{
  options: PickerOption[];
  value: number | string | null;
  onChange: (id: number | string | null) => void;
  placeholder?: string;
  /** Render a "None" row at the top of the menu (employee variant). */
  includeNone?: boolean;
  noneLabel?: string;
  disabled?: boolean;
}> = ({ options, value, onChange, placeholder = 'Search…', includeNone, noneLabel = 'None (no link)', disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuStyle = useFloatingMenu(open, rootRef);

  const selected = useMemo(() => options.find((o) => o.id === value) ?? null, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      o.name.toLowerCase().includes(q) || (o.sub ?? '').toLowerCase().includes(q));
  }, [options, query]);

  // Outside-click closes the menu.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => { setHi(0); }, [query, open]);

  const pick = (id: number | string | null) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  // Selected pill state.
  if (selected && !open) {
    return (
      <div className="picker" ref={rootRef}>
        <div className="picker-selected">
          <Avatar first={selected.initials ? selected.initials[0] : selected.name} sm />
          <div className="grow">
            <div className="nm" style={{ fontWeight: 600 }}>{selected.name}</div>
            {selected.sub && <div className="sub" style={{ fontSize: 12, color: 'var(--text-3)' }}>{selected.sub}</div>}
          </div>
          {!disabled && (
            <button type="button" className="rm" aria-label="Clear selection" onClick={() => pick(null)}><X /></button>
          )}
        </div>
      </div>
    );
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    const menuLen = filtered.length + (includeNone ? 1 : 0);
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(menuLen - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (includeNone && hi === 0) { pick(null); return; }
      const opt = filtered[hi - (includeNone ? 1 : 0)];
      if (opt) pick(opt.id);
    }
  };

  return (
    <div className={`picker${open ? ' open' : ''}`} ref={rootRef}>
      <div className="picker-input" onClick={() => !disabled && setOpen(true)}>
        <Search />
        <input
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
        />
      </div>
      {open && (
        <div className="picker-menu" style={menuStyle}>
          {includeNone && (
            <button
              type="button"
              className={`picker-opt${hi === 0 ? ' hi' : ''}`}
              onMouseEnter={() => setHi(0)}
              onClick={() => pick(null)}
            >
              <span className="grow picker-none" style={{ padding: 0 }}>{noneLabel}</span>
              {value === null && <Check />}
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="picker-none">No matches</div>
          ) : (
            filtered.map((o, idx) => {
              const i = idx + (includeNone ? 1 : 0);
              return (
                <button
                  key={o.id}
                  type="button"
                  className={`picker-opt${hi === i ? ' hi' : ''}`}
                  onMouseEnter={() => setHi(i)}
                  onClick={() => pick(o.id)}
                >
                  <Avatar first={o.initials ? o.initials[0] : o.name} sm />
                  <span className="grow">
                    <span className="nm">{o.name}</span>
                    {o.sub && <span className="sub"> · {o.sub}</span>}
                  </span>
                  {o.id === value && <Check />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ── Multi-select chip picker (WP9c; read-only + keyboard for Employee Roles) ──
export const MultiSelectPicker: React.FC<{
  options: PickerOption[];
  values: (number | string)[];
  onChange: (ids: (number | string)[]) => void;
  placeholder?: string;
  /** Input placeholder once at least one value is selected. */
  addPlaceholder?: string;
  /** Read-only: chips without ×, no input; `emptyLabel` when nothing is selected. */
  disabled?: boolean;
  emptyLabel?: string;
  /** Avatar initials on chips/options (people pickers). Off → a plain + marker. */
  showAvatars?: boolean;
}> = ({
  options, values, onChange, placeholder = 'Add employees…', addPlaceholder = 'Add another…',
  disabled, emptyLabel = 'None', showAvatars = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuStyle = useFloatingMenu(open && !disabled, rootRef);

  const selectedOpts = options.filter((o) => values.includes(o.id));
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) =>
      !values.includes(o.id) &&
      (!q || o.name.toLowerCase().includes(q) || (o.sub ?? '').toLowerCase().includes(q)));
  }, [options, values, query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => { setHi(0); }, [query, open]);

  const add = (id: number | string) => { onChange([...values, id]); setQuery(''); };
  const remove = (id: number | string) => onChange(values.filter((v) => v !== id));

  if (disabled) {
    return (
      <div className="picker">
        <div className="chip-field ro">
          {selectedOpts.length === 0
            ? <span className="muted">{emptyLabel}</span>
            : selectedOpts.map((o) => <span key={o.id} className="sel-chip ro">{o.name}</span>)}
        </div>
      </div>
    );
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); e.currentTarget.blur(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(filtered.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[hi];
      if (opt) add(opt.id);
    } else if (e.key === 'Backspace' && !query && selectedOpts.length) {
      remove(selectedOpts[selectedOpts.length - 1].id);
    }
  };

  const noneText = query.trim()
    ? `No matches for “${query.trim()}”`
    : options.length > 0 ? 'Everything is already added' : 'No matches';

  return (
    <div className="picker" ref={rootRef}>
      <div className={`chip-field${open ? ' focus' : ''}`} onClick={() => { setOpen(true); inputRef.current?.focus(); }}>
        {selectedOpts.map((o) => (
          <span key={o.id} className="sel-chip">
            {o.name}
            <button type="button" className="rm" aria-label={`Remove ${o.name}`} onClick={(e) => { e.stopPropagation(); remove(o.id); }}><X /></button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={query}
          placeholder={selectedOpts.length ? addPlaceholder : placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
        />
      </div>
      {open && (
        <div className="picker-menu" style={menuStyle}>
          {filtered.length === 0 ? (
            <div className="picker-none">{noneText}</div>
          ) : (
            filtered.map((o, i) => (
              <button
                key={o.id}
                type="button"
                className={`picker-opt${i === hi ? ' hi' : ''}`}
                onMouseEnter={() => setHi(i)}
                // Keep focus in the input so the keyboard flow continues after a click.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add(o.id)}
              >
                {showAvatars ? <Avatar first={o.name} sm /> : <Plus />}
                <span className="grow">
                  <span className="nm">{o.name}</span>
                  {o.sub && <span className="sub"> · {o.sub}</span>}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ── Modal shell (accent + danger variants) ────────────────────────────────────
export const ModalShell: React.FC<{
  open: boolean;
  onClose: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  variant?: 'accent' | 'danger';
  wide?: boolean;
  footer?: React.ReactNode;
  children: React.ReactNode;
}> = ({ open, onClose, icon, title, subtitle, variant = 'accent', wide, footer, children }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true">
        <div className={`modal-head${variant === 'danger' ? ' danger' : ''}`}>
          <span className="mi">{icon}</span>
          <div>
            <h3>{title}</h3>
            {subtitle && <div className="ms">{subtitle}</div>}
          </div>
          <button type="button" className="x" aria-label="Close" onClick={onClose}><X /></button>
        </div>
        {children}
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
};
